import { z } from 'zod';

import { ApiOk, IsoDate, Locale, Role, SessionRole, Uid, Ulid } from './primitives';

/** User as visible to other members (queue "added by", pairing exchange). */
export const PublicUserDTO = z.object({
  id: Uid,
  displayName: z.string().nullable(),
  photoUrl: z.string().nullable(),
  isAnonymous: z.boolean(),
});
export type PublicUserDTO = z.infer<typeof PublicUserDTO>;

/** The caller's own profile (`GET /me`). */
export const UserDTO = PublicUserDTO.extend({
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  role: Role,
  locale: Locale,
  createdAt: IsoDate,
  lastLoginAt: IsoDate.nullable(),
});
export type UserDTO = z.infer<typeof UserDTO>;

export const SubscriptionStatus = z.enum(['active', 'trialing', 'past_due', 'canceled', 'expired']);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

export const SubscriptionSource = z.enum(['stripe', 'grant', 'manual']);
export type SubscriptionSource = z.infer<typeof SubscriptionSource>;

export const ProductKind = z.enum(['plan', 'addon']);
export type ProductKind = z.infer<typeof ProductKind>;

/** One `subscriptions` row as returned to its owner. */
export const SubscriptionDTO = z.object({
  id: Ulid,
  productId: z.string(),
  productKind: ProductKind,
  productName: z.object({ en: z.string(), es: z.string() }),
  parentSubscriptionId: Ulid.nullable(),
  status: SubscriptionStatus,
  source: SubscriptionSource,
  features: z.array(z.string()),
  currentPeriodStart: IsoDate,
  currentPeriodEnd: IsoDate.nullable(),
  cancelAt: IsoDate.nullable(),
  canceledAt: IsoDate.nullable(),
});
export type SubscriptionDTO = z.infer<typeof SubscriptionDTO>;

/**
 * "My plan" for the standalone web (docs/13 §5.2, openapi `PlanSummary`). Use this and not
 * `features`: during a session `features` is the host's snapshot, so a guest in a paid room has
 * features and no subscription.
 */
export const PlanSummary = z.object({
  tier: z.enum(['free', 'premium']),
  status: SubscriptionStatus.nullable(),
  interval: z.enum(['month', 'year']).nullable(),
  trialEnd: IsoDate.nullable(),
  currentPeriodEnd: IsoDate.nullable(),
  cancelAtPeriodEnd: z.boolean(),
  source: SubscriptionSource.nullable(),
  paymentMethod: z.object({ brand: z.string(), last4: z.string() }).nullable(),
});
export type PlanSummary = z.infer<typeof PlanSummary>;

/**
 * `GET /me`.
 *
 * `user` is always present: for a TV token it is the **host user of the token's session**
 * (a TV has no `users` row of its own, docs/03 §1), so the TV can render the profile screen.
 * `features` are the caller's effective features (session snapshot or own subscriptions,
 * docs/07 §1) and `subscriptions` are the currently-active rows of that same user.
 * `activeSession.role` is `host` for a TV token and for the session's host user.
 */
export const MeResponse = z.object({
  user: UserDTO,
  features: z.array(z.string()),
  subscriptions: z.array(SubscriptionDTO),
  /** The caller's own plan (docs/13 §5.2). The API always sends it; optional for older APIs. */
  plan: PlanSummary.optional(),
  activeSession: z.object({ id: Ulid, role: SessionRole }).optional(),
});
export type MeResponse = z.infer<typeof MeResponse>;

/** `PATCH /me` */
export const MePatchBody = z
  .object({
    displayName: z.string().trim().min(1).max(60),
    photoPath: z.string().min(1).max(512).nullable(),
    locale: Locale,
  })
  .partial();
export type MePatchBody = z.infer<typeof MePatchBody>;
export const MePatchResponse = UserDTO;
export type MePatchResponse = z.infer<typeof MePatchResponse>;

/** `POST /me/photo` (multipart field `file`) */
export const MePhotoResponse = z.object({
  photoPath: z.string(),
  photoUrl: z.string(),
});
export type MePhotoResponse = z.infer<typeof MePhotoResponse>;

/**
 * `POST /me/logout` → `204 No Content` (the route is declared `responseKind: 'empty'`, so the
 * client resolves `void` and this schema is never applied to a payload).
 */
export const MeLogoutResponse = z.undefined();
export type MeLogoutResponse = z.infer<typeof MeLogoutResponse>;

/** `DELETE /me` — soft delete + Firebase account deletion (docs/03 §2). */
export const MeDeleteResponse = ApiOk;
export type MeDeleteResponse = z.infer<typeof MeDeleteResponse>;
